<template>
    <div class="flex flex-col dark:text-white">
        <div class="my-5 flex items-center justify-between">
            <div>
                <router-link :to="backRoute" class="text-sm text-indigo-500 hover:underline">
                    &larr; {{ backLabel }}
                </router-link>
                <h1 class="mt-1 text-2xl font-semibold">Envio FDIC #{{ $route.params.id }}</h1>
            </div>
            <div class="flex flex-col items-end gap-1">
                <button
                    :disabled="retrying || !canRetry"
                    @click="retry"
                    class="rounded-md bg-indigo-500 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-indigo-400 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {{ retrying ? 'Reenviando...' : 'Reenviar' }}
                </button>
                <small v-if="retryTooltip" class="text-xs text-gray-400 dark:text-gray-500">
                    {{ retryTooltip }}
                    <router-link
                        v-if="submission && submission.latest_submission_id"
                        :to="{ name: 'fdic.detail', params: { id: submission.latest_submission_id } }"
                        class="text-indigo-500 hover:underline"
                    >Ver mais recente →</router-link>
                </small>
            </div>
        </div>

        <div v-if="loading" class="text-sm">Carregando...</div>

        <div v-else-if="submission" class="space-y-4">
            <section class="rounded-lg border p-4 dark:border-gray-700">
                <h2 class="font-semibold">Resumo</h2>
                <dl class="mt-2 grid grid-cols-2 gap-2 text-sm">
                    <dt class="text-gray-500">Status</dt>
                    <dd>{{ submission.status }}</dd>
                    <dt class="text-gray-500">Tipo</dt>
                    <dd>{{ submission.type }}</dd>
                    <dt class="text-gray-500">Ticket</dt>
                    <dd>{{ submission.ticket || '—' }}</dd>
                    <dt class="text-gray-500">Usuário</dt>
                    <dd>
                        <router-link
                            v-if="submission.user"
                            :to="{ name: 'user.details', params: { user_id: submission.user.id } }"
                            target="_blank"
                            class="text-indigo-500 hover:underline"
                        >
                            {{ submission.user.name || submission.user.email || `#${submission.user.id}` }}
                        </router-link>
                        <span v-else>#{{ submission.user_id }}</span>
                    </dd>
                    <dt class="text-gray-500">Documento</dt>
                    <dd>{{ submission.user?.cpf || submission.user?.cnpj || '—' }}</dd>
                    <dt class="text-gray-500">Email</dt>
                    <dd>{{ submission.user?.email || '—' }}</dd>
                    <dt class="text-gray-500">Tenant ID</dt>
                    <dd>{{ submission.tenant_id || '—' }}</dd>
                    <dt class="text-gray-500">Enviado em</dt>
                    <dd>{{ formatDate(submission.submitted_at) }}</dd>
                    <dt class="text-gray-500">Resolvido em</dt>
                    <dd>{{ formatDate(submission.resolved_at) }}</dd>
                </dl>
            </section>

            <section v-if="sortedErrors.length" class="rounded-lg border p-4 dark:border-gray-700">
                <h2 class="font-semibold">Erros e alertas retornados ({{ sortedErrors.length }})</h2>
                <ul class="mt-3 space-y-2 text-sm">
                    <li
                        v-for="(err, idx) in sortedErrors"
                        :key="idx"
                        :class="errorClasses(err)"
                        class="flex items-start gap-2 rounded-md border p-2"
                    >
                        <span
                            :class="errorBadgeClasses(err)"
                            class="shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold uppercase"
                        >{{ (err.tipo || err.type || 'erro') }}</span>
                        <span class="break-words">{{ err.msg || err.message }}</span>
                    </li>
                </ul>
            </section>

            <section class="rounded-lg border p-4 dark:border-gray-700">
                <h2 class="font-semibold">Payload enviado</h2>
                <pre class="mt-2 overflow-auto rounded bg-gray-900 p-3 text-xs text-green-300">{{ formatJson(submission.request_payload) }}</pre>
            </section>

            <section v-if="submission.response_payload" class="rounded-lg border p-4 dark:border-gray-700">
                <h2 class="font-semibold">Resposta BDR</h2>
                <pre class="mt-2 overflow-auto rounded bg-gray-900 p-3 text-xs text-yellow-300">{{ formatJson(submission.response_payload) }}</pre>
            </section>
        </div>
    </div>
</template>

<script>
import { useToast } from "vue-toastification";

export default {
    name: "FdicSubmissionDetail",
    setup() {
        const toast = useToast();
        return { toast };
    },
    data() {
        return {
            loading: false,
            retrying: false,
            submission: null,
        };
    },
    computed: {
        backRoute() {
            const userId = this.$route.query.userId;
            if (this.$route.query.from === 'history' && userId) {
                return { name: 'fdic.history', params: { userId } };
            }
            return { name: 'fdic.submissions' };
        },
        backLabel() {
            return this.$route.query.from === 'history'
                ? 'Voltar para histórico'
                : 'Voltar para cedentes';
        },
        canRetry() {
            return !this.retrying
                && this.submission
                && this.submission.is_latest
                && ['rejected', 'error', 'approved_with_warnings'].includes(this.submission.status);
        },
        retryTooltip() {
            if (!this.submission) return '';
            if (!this.submission.is_latest) return 'Existe uma submissão mais recente para este usuário.';
            if (!['rejected', 'error', 'approved_with_warnings'].includes(this.submission.status)) {
                return 'O status atual não permite reenvio.';
            }
            return '';
        },
        sortedErrors() {
            const errors = this.submission?.errors ?? [];
            if (!Array.isArray(errors)) return [];
            // Severity: blocking (3) > warning (2) > info (1). Maior primeiro.
            return [...errors].sort((a, b) => this.severityOf(b) - this.severityOf(a));
        },
    },
    watch: {
        '$route.params.id'() {
            this.fetch();
        },
    },
    mounted() {
        this.fetch();
    },
    methods: {
        async fetch() {
            this.loading = true;
            try {
                const { data } = await window.axios.get(`/api/fdic/submissions/${this.$route.params.id}`);
                this.submission = data;
            } finally {
                this.loading = false;
            }
        },
        async retry() {
            this.retrying = true;
            try {
                const { data } = await window.axios.post(`/api/fdic/submissions/${this.$route.params.id}/retry`);
                const newId = data?.submission?.id;
                const status = data?.submission?.status;
                if (newId) {
                    this.toast.success("Reenvio enfileirado com sucesso.");
                    this.$router.push({ name: 'fdic.detail', params: { id: newId } });
                } else {
                    this.toast.warning("Reenvio processado, mas sem nova submission retornada.");
                    this.fetch();
                }
            } catch (e) {
                const msg = e?.response?.data?.message ?? "Erro ao reenviar. Tente novamente.";
                this.toast.error(msg);
            } finally {
                this.retrying = false;
            }
        },
        formatDate(iso) {
            if (!iso) return "—";
            return new Date(iso).toLocaleString("pt-BR");
        },
        formatJson(obj) {
            if (!obj) return "";
            return JSON.stringify(obj, null, 2);
        },
        severityOf(err) {
            const t = (err?.tipo || err?.type || "").toLowerCase();
            // 3 = bloqueador, 2 = alerta, 1 = info
            if (["layout", "validação", "validacao", "processamento", "fundo"].includes(t)) return 3;
            if (["alerta", "dados bancários", "dados_bancarios", "precondition"].includes(t)) return 2;
            return 1;
        },
        errorClasses(err) {
            const s = this.severityOf(err);
            if (s === 3) return "border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-700";
            if (s === 2) return "border-yellow-300 bg-yellow-50 dark:bg-yellow-900/20 dark:border-yellow-700";
            return "border-gray-300 bg-gray-50 dark:bg-gray-800 dark:border-gray-700";
        },
        errorBadgeClasses(err) {
            const s = this.severityOf(err);
            if (s === 3) return "bg-red-200 text-red-800";
            if (s === 2) return "bg-yellow-200 text-yellow-800";
            return "bg-gray-200 text-gray-800";
        },
    },
};
</script>
